/**
 * Listado de una linea completa, con el indice de sus categorias arriba.
 *
 * El indice de categorias no es decorativo: es lo que reparte la autoridad de
 * la linea hacia cada categoria y lo que le da al comprador la forma de bajar
 * un nivel sin volver al mega menu.
 */
import { Link, useParams, useSearchParams } from 'react-router-dom';

import type { OrdenCatalogo } from '@bandu/compartido';

import { ListadoProductos } from '../componentes/catalogo/ListadoProductos';
import { AvisoError, Cargando } from '../componentes/comunes/EstadoPeticion';
import { Migas } from '../componentes/diseno/Migas';
import { useDespacho } from '../contexto/despacho-contexto';
import { usePeticion } from '../hooks/use-peticion';
import { DatosEstructurados } from '../seo/DatosEstructurados';
import { esquemaListaProductos, esquemaMigas } from '../seo/esquemas';
import { Seo } from '../seo/Seo';
import { obtenerLinea } from '../servicios/catalogo.servicio';
import { anuncioEntrega } from '../utilidades/entrega';
import { numero } from '../utilidades/formato';
import { rutas } from '../utilidades/rutas';

export function Linea() {
  const { slug = '' } = useParams();
  const [parametros] = useSearchParams();
  const pagina = Number(parametros.get('pagina') ?? '1');
  const orden = parametros.get('orden') ?? undefined;
  const { opciones: opcionesDespacho } = useDespacho();

  const { estado, datos, error } = usePeticion(
    (senal) =>
      obtenerLinea(slug, {
        pagina,
        ...(orden !== undefined ? { orden: orden as OrdenCatalogo } : {}),
        senal,
      }),
    [slug, pagina, orden],
  );

  if (estado === 'cargando') {
    return (
      <div className="contenedor pagina">
        <Cargando que="la linea" />
      </div>
    );
  }

  if (datos === null) {
    return (
      <div className="contenedor pagina">
        <Seo
          titulo="Linea no encontrada"
          descripcion="La linea que buscas no existe en el catalogo."
          ruta={rutas.linea(slug)}
          sinIndexar
        />
        <AvisoError error={error} />
      </div>
    );
  }

  const { linea, categorias, productos } = datos;
  const migas = [
    { nombre: 'Inicio', ruta: rutas.inicio() },
    { nombre: linea.nombre, ruta: rutas.linea(linea.slug) },
  ];

  return (
    <>
      <Seo
        titulo={linea.nombre}
        descripcion={`${numero(productos.total)} productos de ${linea.nombre} en Bandu: equipo probado en el río con stock real${anuncioEntrega(opcionesDespacho)}.`}
        ruta={rutas.linea(linea.slug)}
      />
      <DatosEstructurados
        esquemas={[esquemaMigas(migas), esquemaListaProductos(linea.nombre, productos.items)]}
      />

      <div className="contenedor pagina">
        <Migas migas={migas} />

        <header className="encabezado-pag">
          <div>
            <h1>{linea.nombre}</h1>
            <p className="sub">
              {numero(categorias.length)} categorias · {numero(productos.total)}{' '}
              {productos.total === 1 ? 'producto' : 'productos'}
            </p>
          </div>
        </header>

        {categorias.length > 0 ? (
          <nav className="tira-categorias" aria-label={`Categorias de ${linea.nombre}`}>
            <ul className="tira-marcas">
              {categorias.map((categoria) => (
                <li key={categoria.id}>
                  <Link className="marca-pil" to={rutas.categoria(categoria.slug)}>
                    {categoria.nombre}
                    <span className="cuenta">{numero(categoria.cantidadProductos)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        <ListadoProductos
          productos={productos}
          titulo={linea.nombre}
          ruta={rutas.linea(linea.slug)}
        />
      </div>
    </>
  );
}
